import * as React from 'react'
import { useDispatch } from 'react-redux'
import SportsGroupComposition from 'components/specific/favorite-sports/sport-group-composition'
import Panel from 'components/common/settings-panel/settings-panel'
import useSelector from 'core/hooks/useSelector'
import { IErrors } from 'pages/templates/templates'
import { getSynchronizedFilters } from 'core/utils/filters'
import { FilterKey, PartialFilters, setFilters } from 'core/redux/reducers/filtersSlice'
import { ISportsGroup } from 'core/models/api/sports-group/sports-group-piloting'
import { useApiDeleteSportsGroup } from 'core/api/sports-group/useApiDeleteSportsGroup'
import { useApiPostSportsGroup } from 'core/api/sports-group/useApiPostSportsGroup'
import IFilters from '../../../core/models/filters'
import { useTranslation } from 'react-i18next'

interface ISportsGroupPanelProps {
  sportsGroup: ISportsGroup
  onClose: () => void
  onSportsGroupChange: (sportsGroup: ISportsGroup | Partial<ISportsGroup>) => void
  errors?: IErrors
  setIsActiveTab: React.Dispatch<React.SetStateAction<string>>
}

interface IDumbSportsGroupPanelProps extends ISportsGroupPanelProps {
  onSave: () => void
  onDelete: () => void
  isLoading: boolean
}

const DumbSportsGroupPanel = ({
  sportsGroup,
  onClose,
  onSportsGroupChange,
  onSave,
  onDelete,
  isLoading,
  errors
}: IDumbSportsGroupPanelProps) => {
  const { t } = useTranslation()
  const currentStoreId = useSelector(state => state.filters.store_id)

  const canBeSaved = React.useCallback(
    (hasBeenEdited: boolean) =>
      sportsGroup ? hasBeenEdited && sportsGroup.label && sportsGroup.composition.length >= 2 : false,
    [sportsGroup]
  )
  return (
    <Panel
      item={sportsGroup}
      onClose={onClose}
      onSave={onSave}
      onDelete={onDelete}
      onChange={onSportsGroupChange}
      isLoading={isLoading}
      canEdit={true}
      defaultTitle={t('settings.sport_groups.new_sport_group')}
      deleteActionLabel={t('settings.sport_groups.delete_sport_group')}
      deleteActionMessage={t('settings.sport_groups.delete.confirm')}
      editTitle={t('settings.sport_groups.title')}
      editFieldLabel={t('settings.sport_groups.name_sport_group')}
      checkCanBeSaved={canBeSaved}
      errors={errors}
      groupType="Sport"
    >
      <>
        <label className="mg-groups-label vtmn-typo_text-3">{t('settings.sport_groups.sport_group_composition')}</label>

        <SportsGroupComposition
          isEditable={true}
          sportsGroup={sportsGroup}
          onSportsGroupChange={onSportsGroupChange}
          currentStoreId={currentStoreId}
          isFetchingSports={false}
        />
      </>
    </Panel>
  )
}

const SportsGroupPanel = (props: ISportsGroupPanelProps) => {
  const { sportsGroup, onClose, setIsActiveTab } = props
  const sessionFilters = useSelector<PartialFilters>(state => state.filters)
  const dispatch = useDispatch()
  const postMutation = useApiPostSportsGroup()
  const deleteMutation = useApiDeleteSportsGroup()

  const handleSave = () => {
    postMutation.mutate(sportsGroup, {
      onSuccess: () => {
        onClose()
      }
    })
  }

  const handleDelete = () => {
    deleteMutation.mutate(sportsGroup, {
      onSuccess: () => {
        if (sportsGroup.id === sessionFilters.sports_group) {
          const newFilters = getSynchronizedFilters({ [FilterKey.SPORTS_GROUP]: null } as unknown as PartialFilters)
          const nextFilters = { ...sessionFilters, ...newFilters } as IFilters
          dispatch(setFilters(nextFilters))
        }
        onClose()
        setIsActiveTab('read')
      }
    })
  }

  return (
    <DumbSportsGroupPanel
      onSave={handleSave}
      onDelete={handleDelete}
      isLoading={postMutation.isPending || deleteMutation.isPending}
      {...props}
    />
  )
}

export default SportsGroupPanel
